// Utility functions for file management and detection
import firebaseService from '../firebase.service';

// Fetch the full set of cover and model filenames for a project in two
// listAll calls, then return a lookup function that resolves instantly.
export const buildMediaIndex = async (project = 'fbf2024') => {
  const projectFolder = project.toUpperCase();

  const [coverFiles, modelFiles] = await Promise.all([
    firebaseService.listFileNames(`${projectFolder}/covers`).catch(() => []),
    firebaseService.listFileNames(`${projectFolder}/models`).catch(() => [])
  ]);

  const coverSet = new Set(coverFiles.map(f => f.replace(/\.webp$/i, '')));
  const modelSet = new Set(modelFiles.map(f => f.replace(/\.glb$/i, '')));

  console.log(`📂 Media index built for ${projectFolder}: ${coverSet.size} covers, ${modelSet.size} models`);

  const lookup = (bookCode) => {
    if (modelSet.has(bookCode)) return 'glb';
    if (coverSet.has(bookCode)) return 'image';
    return 'none';
  };

  return { lookup, coverSet, modelSet };
};

// Single-book detection – kept for call-sites that only need one check.
// Uses listAll under the hood via buildMediaIndex so it's still reliable.
export const detectMediaType = async (bookCode, project = 'fbf2024') => {
  const { lookup } = await buildMediaIndex(project);
  return lookup(bookCode);
};

export const getImagePath = async (bookCode, project = 'fbf2024') => {
  const projectFolder = project.toUpperCase(); // fbf2024 -> FBF2024
  
  // Try different image formats in order of preference
  const formats = ['webp', 'png', 'jpg', 'jpeg'];
  
  for (const format of formats) {
    try {
      const path = `${projectFolder}/books/${bookCode}.${format}`;
      const url = await firebaseService.getFileUrl(path);
      if (url) {
        return path;
      }
    } catch (error) {
      // Continue to next format
    }
  }
  
  // Default fallback
  return `${projectFolder}/books/${bookCode}.webp`;
};

export const getGLBPath = (bookCode, project = 'fbf2024') => {
  const projectFolder = project.toUpperCase(); // fbf2024 -> FBF2024
  return `${projectFolder}/models/${bookCode}.glb`;
};

export const getCompanyImagePath = (companyCode, project = 'fbf2024') => {
  const projectFolder = project.toUpperCase(); // fbf2024 -> FBF2024
  return `${projectFolder}/books/${companyCode}.webp`;
};

export const convertToWebP = async (file) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert to WebP'));
        }
      }, 'image/webp', 0.8);
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

export const validateFileType = (file, allowedTypes) => {
  const fileExtension = file.name.toLowerCase().split('.').pop();
  return allowedTypes.includes(fileExtension);
};

export const getFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
